import { useLingui } from '@lingui/react/macro'
import { cva } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/src/designSystem/lib/utils'

import { Button } from './Button'
import { Calendar } from './Calendar'
import { Field, FieldLabel } from './Field'
import { Popover, PopoverContent, PopoverTrigger } from './Popover'

const datePickerFieldVariants = cva('', {
  variants: {
    width: {
      default: 'w-44',
      full: 'w-full',
    },
  },
  defaultVariants: {
    width: 'default',
  },
})

export const DatePicker: React.FC<{
  date?: Date | string
  onDateChange?: (date: Date | undefined) => void
  label?: string
  /** Use inline mode when inside a Sheet/Dialog to avoid popover positioning issues */
  inline?: boolean
  /** Compact variant for dense surfaces: sm trigger button + smaller label. */
  size?: 'default' | 'sm'
  /** 'full' fills the container instead of the fixed default width. */
  width?: 'default' | 'full'
}> = ({ date, onDateChange, label, inline, size = 'default', width }) => {
  const { t } = useLingui()
  const [open, setOpen] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)
  // Unique per instance so multiple pickers (e.g. a From/To pair) each get a
  // correctly associated label in both the inline and popover paths.
  const buttonId = React.useId()
  const dateObj =
    date instanceof Date ? date : date ? new Date(date) : undefined

  React.useEffect(() => {
    if (!inline || !open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [inline, open])

  const labelClassName =
    size === 'sm' ? 'text-xxs text-muted-foreground' : undefined

  const calendar = (
    <Calendar
      mode='single'
      selected={dateObj}
      defaultMonth={dateObj}
      captionLayout='dropdown'
      onSelect={(newDate: Date | undefined) => {
        onDateChange?.(newDate)
        setOpen(false)
      }}
    />
  )

  if (inline) {
    return (
      <Field className={cn('shrink-0', datePickerFieldVariants({ width }))}>
        {label && (
          <FieldLabel htmlFor={buttonId} className={labelClassName}>
            {label}
          </FieldLabel>
        )}
        <div ref={containerRef} className='relative'>
          <Button
            variant='outline'
            size={size === 'sm' ? 'sm' : 'default'}
            id={buttonId}
            className='w-full justify-start font-normal'
            onClick={() => setOpen((o) => !o)}>
            {dateObj ? dateObj.toLocaleDateString() : t`Select date`}
          </Button>
          {open && (
            <div className='absolute left-0 top-full z-100 mt-1 rounded-md border bg-popover p-0 shadow-md'>
              {calendar}
            </div>
          )}
        </div>
      </Field>
    )
  }

  return (
    <Field className={cn('mx-auto', datePickerFieldVariants({ width }))}>
      {label && (
        <FieldLabel htmlFor={buttonId} className={labelClassName}>
          {label}
        </FieldLabel>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant='outline'
            size={size === 'sm' ? 'sm' : 'default'}
            id={buttonId}
            className='justify-start font-normal'>
            {dateObj ? dateObj.toLocaleDateString() : t`Select date`}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className='z-100 w-auto overflow-hidden p-0'
          align='start'>
          {calendar}
        </PopoverContent>
      </Popover>
    </Field>
  )
}
